import { useEffect } from 'react';

/**
 * Custom hook to add structured data to the document head
 * @param {Object|Array} structuredData - The structured data object(s) to add
 * @param {string} id - Unique identifier for the script tag
 */
export const useStructuredData = (structuredData, id = 'structured-data') => {
  useEffect(() => {
    if (!structuredData) return;

    // Remove existing structured data script if it exists
    const existingScript = document.getElementById(id);
    if (existingScript) {
      existingScript.remove();
    }

    // Create new script element
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    
    // Handle both single objects and arrays of structured data
    const data = Array.isArray(structuredData) ? structuredData : [structuredData];
    script.innerHTML = JSON.stringify(data.length === 1 ? data[0] : data, null, 2);
    
    // Add to document head
    document.head.appendChild(script);

    // Cleanup function to remove script when component unmounts
    return () => {
      const scriptToRemove = document.getElementById(id);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [structuredData, id]);
};

/**
 * Custom hook specifically for adding multiple structured data schemas
 * @param {Array} schemas - Array of structured data objects
 * @param {string} baseId - Base ID for script tags (will be appended with index)
 */
export const useMultipleStructuredData = (schemas, baseId = 'schema') => {
  useEffect(() => {
    if (!schemas || !Array.isArray(schemas)) return;

    // Remove existing scripts
    schemas.forEach((_, index) => {
      const existingScript = document.getElementById(`${baseId}-${index}`);
      if (existingScript) {
        existingScript.remove();
      }
    });

    // Add new scripts
    schemas.forEach((schema, index) => {
      if (schema) {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = `${baseId}-${index}`;
        script.innerHTML = JSON.stringify(schema, null, 2);
        document.head.appendChild(script);
      }
    });

    // Cleanup function
    return () => {
      schemas.forEach((_, index) => {
        const scriptToRemove = document.getElementById(`${baseId}-${index}`);
        if (scriptToRemove) {
          scriptToRemove.remove();
        }
      });
    };
  }, [schemas, baseId]);
};